/**
 * The only module in the codebase allowed to touch an icon file.
 *
 * Every glyph of UI iconography comes from the vendored HackerNoon
 * pixel-icon-library (`public/icons/`). No hand-authored SVG, no icon font, no
 * emoji, no dingbat stand-ins. If a role has no matching icon, compose two real
 * icons with `iconStack` rather than inventing one.
 *
 * Tinting goes through CSS `mask-image` + `background-color`, never `<img>`.
 * A 1-bit pixel icon then takes any phosphor colour and can carry the same
 * bloom skirt as the playfield, so the HUD and the tube read as one image.
 */

import { ROLE, hex, roleHex, type PhosphorName, type Role } from '@/core/palette';
import {
  REGULAR_ICON_SET,
  SOLID_ICON_SET,
  type RegularIconName,
  type SolidIconName,
} from './iconNames';

export type { SolidIconName, RegularIconName };

/** The vocabulary every panel speaks. Solid is the default weight. */
export type IconName = SolidIconName;

export type IconVariant = 'solid' | 'regular';

/**
 * Integer sizes only. The source art sits on a 24-unit grid, so 12/24/48 land
 * exactly on it; 16 and 32 are available for dense rows and accept a half-unit
 * edge. Nothing else is allowed — fractional sizes resample pixel art to mush.
 */
export type IconSize = 12 | 16 | 24 | 32 | 48;

export interface IconOptions {
  size?: IconSize;
  variant?: IconVariant;
  /** Semantic palette role. Preferred over `colour`. */
  role?: Role;
  /** Raw phosphor slot, when no role fits. */
  phosphor?: PhosphorName;
  /** Explicit CSS colour. Escape hatch for state tints already in CSS vars. */
  colour?: string;
  /** Phosphor bloom, 0 = flat, 1 = full tube bloom. */
  glow?: number;
  /** Accessible label. Without one the icon is marked decorative. */
  title?: string;
  /** Extra class names, space separated. */
  className?: string;
  /** Continuous rotation, for the busy spinner. Respects reduced motion. */
  spin?: boolean;
}

const DEV = import.meta.env?.DEV ?? false;

/** Resolves an icon file against the document base so `base: './'` builds work. */
export function iconUrl(name: string, variant: IconVariant = 'solid'): string {
  const file = variant === 'solid' ? `${name}-solid.svg` : `${name}.svg`;
  return new URL(`icons/${variant}/${file}`, document.baseURI).href;
}

export function isIconName(name: string): name is IconName {
  return SOLID_ICON_SET.has(name);
}

/**
 * Game data names icons the way the files are named on disk
 * (`octagon-times-solid`), while the union holds the weight-free id
 * (`octagon-times`). Without this every tower in the shop and every upgrade in
 * the panel resolved to the `shapes` fallback — ten towers wearing one icon.
 */
export function normaliseIconId(id: string): string {
  if (SOLID_ICON_SET.has(id)) return id;
  const bare = id.replace(/-(?:solid|regular)$/, '');
  return SOLID_ICON_SET.has(bare) ? bare : id;
}

export function hasRegular(name: string): name is RegularIconName {
  return REGULAR_ICON_SET.has(name);
}

function assertIcon(name: string, variant: IconVariant): void {
  const known = variant === 'solid' ? SOLID_ICON_SET.has(name) : REGULAR_ICON_SET.has(name);
  if (known) return;
  const near = nearest(name, variant);
  throw new Error(
    `[ui/Icon] "${name}" is not in the vendored pixel-icon-library (${variant}). ` +
      `Pick a real icon or compose two. Closest ids: ${near.join(', ')}.`,
  );
}

/** Cheap suggestion list so a bad id fails with something actionable. */
function nearest(name: string, variant: IconVariant): string[] {
  const pool = variant === 'solid' ? SOLID_ICON_SET : REGULAR_ICON_SET;
  const head = name.slice(0, 3);
  const out: string[] = [];
  for (const candidate of pool) {
    if (candidate.startsWith(head) || candidate.includes(name)) out.push(candidate);
    if (out.length === 5) break;
  }
  return out.length > 0 ? out : ['shapes', 'question', 'cog'];
}

/**
 * Returns null when the caller named no colour, so the element inherits from
 * CSS. That matters: an inline `--pxi-col` would beat rules like
 * `.btn:disabled .pxi` and `.btn--primary .pxi`, and the icon would keep the
 * wrong tint in a state it does not know about.
 */
function resolveColour(o: IconOptions): string | null {
  if (o.colour) return o.colour;
  if (o.phosphor) return hex(o.phosphor);
  if (o.role) return roleHex(o.role);
  return null;
}

/**
 * Builds one icon element.
 *
 * The element is a `<span>` carrying the mask in custom properties, so a state
 * change is a single property write instead of a rebuild.
 */
export function icon(name: IconName, opts: IconOptions = {}): HTMLElement {
  const variant = opts.variant ?? 'solid';
  // Six ids exist only under one weight; fall back rather than render nothing.
  const effective: IconVariant =
    variant === 'regular' && !REGULAR_ICON_SET.has(name) ? 'solid' : variant;
  assertIcon(name, effective);

  const size = opts.size ?? 16;
  const el = document.createElement('span');
  el.className = `pxi pxi-${size}${opts.className ? ` ${opts.className}` : ''}`;
  el.dataset.icon = name;
  el.style.setProperty('--pxi-url', `url("${iconUrl(name, effective)}")`);
  const colour = resolveColour(opts);
  if (colour) el.style.setProperty('--pxi-col', colour);
  if (opts.glow) el.style.setProperty('--pxi-glow', String(opts.glow));
  if (opts.spin) el.classList.add('is-spinning');

  if (opts.title) {
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', opts.title);
  } else {
    el.setAttribute('aria-hidden', 'true');
  }
  return el;
}

/** Swaps the artwork on an existing icon element without touching layout. */
export function setIcon(el: HTMLElement, name: IconName, variant: IconVariant = 'solid'): void {
  const effective: IconVariant =
    variant === 'regular' && !REGULAR_ICON_SET.has(name) ? 'solid' : variant;
  assertIcon(name, effective);
  if (el.dataset.icon === name) return;
  el.dataset.icon = name;
  el.style.setProperty('--pxi-url', `url("${iconUrl(name, effective)}")`);
}

export function setIconColour(el: HTMLElement, colour: string): void {
  el.style.setProperty('--pxi-col', colour);
}

export function setIconRole(el: HTMLElement, role: Role): void {
  el.style.setProperty('--pxi-col', roleHex(role));
}

/**
 * Composes two real icons into one mark: a base with a smaller badge pinned to
 * a corner. This is the sanctioned way to express something the 248-icon set
 * has no single glyph for (boss warning, "cycle this mode", "sell for cash").
 */
export interface StackOptions extends IconOptions {
  badgeSize?: IconSize;
  badgeRole?: Role;
  badgePhosphor?: PhosphorName;
  badgeGlow?: number;
  corner?: 'br' | 'bl' | 'tr' | 'tl';
}

export function iconStack(
  base: IconName,
  badge: IconName,
  opts: StackOptions = {},
): HTMLElement {
  const size = opts.size ?? 24;
  const badgeSize = opts.badgeSize ?? (size >= 32 ? 16 : 12);
  const wrap = document.createElement('span');
  wrap.className = `pxi-stack pxi-stack-${opts.corner ?? 'br'}`;
  wrap.style.setProperty('--pxi-size', `${size}px`);

  const baseEl = icon(base, { ...opts, size, title: undefined, className: 'pxi-stack-base' });
  const badgeEl = icon(badge, {
    size: badgeSize,
    variant: opts.variant,
    role: opts.badgeRole,
    phosphor: opts.badgePhosphor,
    glow: opts.badgeGlow ?? opts.glow,
    className: 'pxi-stack-badge',
  });
  wrap.append(baseEl, badgeEl);

  if (opts.title) {
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', opts.title);
  } else {
    wrap.setAttribute('aria-hidden', 'true');
  }
  return wrap;
}

/**
 * Icon ids arrive from data (`TowerArchetype.icon`, `UpgradeNode.icon`) as plain
 * strings. In dev a bad id is a hard failure so it gets fixed; in a shipped
 * build it degrades to a real fallback icon instead of a blank box.
 */
export function iconFromId(id: string, fallback: IconName, opts: IconOptions = {}): HTMLElement {
  const name = normaliseIconId(id);
  if (isIconName(name)) return icon(name, opts);
  if (DEV) {
    throw new Error(
      `[ui/Icon] data referenced icon "${id}", which is not in the vendored set. ` +
        `Change the catalog entry to a real id (fallback would be "${fallback}").`,
    );
  }
  console.warn(`[ui/Icon] unknown icon id "${id}", falling back to "${fallback}"`);
  return icon(fallback, opts);
}

export function safeIconId(id: string, fallback: IconName): IconName {
  const name = normaliseIconId(id);
  return isIconName(name) ? name : fallback;
}

// ────────────────────────────────────────────────────────────── ui theming ──

/**
 * Publishes the phosphor palette as CSS custom properties.
 *
 * `ui.css` never writes a hex literal; it reads these. One palette revision in
 * `core/palette.ts` then moves the whole cabinet.
 */
let themeInstalled = false;

export function installUiTheme(): void {
  if (themeInstalled) return;
  themeInstalled = true;
  const s = document.documentElement.style;
  for (const role of Object.keys(ROLE) as Role[]) {
    s.setProperty(`--ui-${kebab(role)}`, roleHex(role));
  }
  // Raw slots, for states that are a colour rather than a role (path tracks,
  // rarity tiers, per-tower tints).
  const slots: PhosphorName[] = [
    'black', 'navy', 'green', 'teal', 'maroon', 'purple', 'olive', 'silver',
    'gray', 'blue', 'lime', 'cyan', 'red', 'magenta', 'yellow', 'white',
  ];
  for (const slot of slots) s.setProperty(`--ph-${slot}`, hex(slot));
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

// ───────────────────────────────────────────────────────────── primitives ──

/**
 * Element builder. Lives here rather than in its own file so every panel has
 * exactly one import for "the shared UI base", and so the module list stays the
 * one the architecture contract names.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * `1,234` — comma grouping.
 *
 * This used to insert U+2009 THIN SPACE. In a monospace stack a thin space
 * either takes a full advance or misses the font entirely and falls back at
 * full width, so `$4 200` read as two numbers standing next to each other
 * rather than one four-figure sum. A comma costs the same column and cannot
 * be misread.
 */
export function groupDigits(value: number): string {
  const n = Math.round(value);
  const sign = n < 0 ? '-' : '';
  const digits = Math.abs(n).toString();
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return sign + out;
}

/** `$1,250`. Cash is always prefixed so it never reads as a raw stat. */
export function money(value: number): string {
  return `$${groupDigits(value)}`;
}

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** `var(--ph-lime)` for a phosphor slot coming out of game data. */
export function phosphorVar(name: PhosphorName): string {
  return `var(--ph-${name})`;
}
